import {ObjectId} from 'bson';
import _ from 'lodash';
import {RoleSearchCountrySchema} from './countries';
import {z} from 'zod';
import {zodTypeguard} from './api/helpers';
import {zodEnum} from './util';
import {ObjectIdSchema} from './zod';

// Restricted fields can't be used in scoring and have special export handling.
export function isRestrictedField(field: FieldDefinition) {
  return isResearchField(field);
}

export const GptModelSchema = z.enum([
  'gpt-4o',
  'gpt-4o-mini',
  'gpt-4.1',
  'gpt-4.1-mini',
  'o1',
  'o1-mini',
  'o3-mini',
  'o3',
]);
export type GptModel = z.infer<typeof GptModelSchema>;

const GptTools = ['perplexity', 'tavily', 'roleCount'] as const;
export const GptToolSchema = zodEnum(GptTools);
export type GptTool = z.infer<typeof GptToolSchema>;

export const EnrichedFieldSchema = z.record(z.unknown());
export type EnrichedFields = z.infer<typeof EnrichedFieldSchema>;

// TODO: add support for arrays (e.g. multi-select categories)
export const FieldDataTypes = [
  'date',
  'dateTime',
  'number',
  'string',
  'boolean',
] as const;
const FieldDataTypeSchema = zodEnum(FieldDataTypes);
export type FieldDataType = z.infer<typeof FieldDataTypeSchema>;

export const StatusSchema = z.union([
  z.literal('draft'),
  z.literal('published'),
]);
export type Status = z.infer<typeof StatusSchema>;
export const StatusLabels: Record<Status, string> = {
  draft: 'Draft',
  published: 'Published',
};

const BaseFieldDefinitionSchema = z.object({
  id: z.instanceof(ObjectId),
  label: z.string(),
  dataType: FieldDataTypeSchema,
  timestamp: z.date(),
  status: StatusSchema,

  // overridden by subtypes
  config: z.object({}),
  type: z.string(),
});

export const KeyplayAnalysisFieldSchema = z.union([
  //  account fields
  z.literal('businessAndMarket'),
  z.literal('companyDomain'),
  z.literal('companyName'),
  z.literal('historyAndCurrentState'),
  z.literal('homepageDescription'),
  z.literal('homepageTitle'),
  z.literal('personalityAndCulture'),

  // scoring data fields
  z.literal('employeeCount'),
  z.literal('followerCount'),
  z.literal('locationCountries'),
  z.literal('trafficRank'),
  z.literal('yearFounded'),

  // signals
  z.literal('orgSignals'),
  z.literal('profileSignals'),
  z.literal('relevanceSignals'),
  z.literal('stackSignals'),

  // page text fields
  z.literal('aboutPageText'),
  z.literal('careersPageText'),
  z.literal('contactPageText'),
  z.literal('jobBoardPageText'),
  z.literal('partnersPageText'),
  z.literal('pricingPageText'),
  z.literal('privacyPageText'),
  z.literal('resourcesPageText'),
  z.literal('rootPageText'),
  z.literal('securityPageText'),
  z.literal('supportPageText'),

  // page source fields
  z.literal('rootPageSource'),

  z.literal('dnsRecords'),
]);
export type KeyplayAnalysisField = z.infer<typeof KeyplayAnalysisFieldSchema>;
export const isKeyplayAnalysisField = zodTypeguard(KeyplayAnalysisFieldSchema);

export const KeyplayAnalysisFields = KeyplayAnalysisFieldSchema.options.map(
  (option) => option.value
);
export const KeyplayAnalysisFieldLabels = {
  businessAndMarket: 'Business and Market',
  companyDomain: 'Company Domain',
  companyName: 'Company Name',
  historyAndCurrentState: 'History and Current State',
  homepageDescription: 'Homepage Description',
  homepageTitle: 'Homepage Title',
  personalityAndCulture: 'Personality and Culture',

  employeeCount: 'Employee Count',
  followerCount: 'Follower Count',
  locationCountries: 'Location Countries',
  trafficRank: 'Traffic Rank',
  yearFounded: 'Year Founded',

  orgSignals: 'Org Signals',
  profileSignals: 'Profile Signals',
  relevanceSignals: 'Relevance Signals',
  stackSignals: 'Stack Signals',

  aboutPageText: 'About Page Text',
  careersPageText: 'Careers Page Text',
  contactPageText: 'Contact Page Text',
  jobBoardPageText: 'Job Board Page Text',
  partnersPageText: 'Partners Page Text',
  pricingPageText: 'Pricing Page Text',
  privacyPageText: 'Privacy Page Text',
  resourcesPageText: 'Resources Page Text',
  rootPageText: 'Root Page Text',
  securityPageText: 'Security Page Text',
  supportPageText: 'Support Page Text',

  rootPageSource: 'Root Page Source',

  dnsRecords: 'DNS Records',
} satisfies Record<KeyplayAnalysisField, string>;

export const CustomerAnalysisFieldSchema = z.object({
  fieldId: ObjectIdSchema,
  hash: z.string(),
});
export type CustomerAnalysisField = z.infer<typeof CustomerAnalysisFieldSchema>;
export const isCustomerAnalysisField = zodTypeguard(
  CustomerAnalysisFieldSchema
);

export const AnalysisFieldSchema = z.union([
  KeyplayAnalysisFieldSchema,
  CustomerAnalysisFieldSchema,
]);
export type AnalysisField = z.infer<typeof AnalysisFieldSchema>;

// Field definition configs
const RoleCountFieldDefinitionConfigSchema = z.object({
  include: z.array(z.string()),
  exclude: z.array(z.string()),
  titles: z.array(z.string()),
  countries: z.array(RoleSearchCountrySchema),
});
export type RoleCountFieldDefinitionConfig = z.infer<
  typeof RoleCountFieldDefinitionConfigSchema
>;

const CrmFieldDefinitionConfigSchema = z.object({
  fieldName: z.string(),
  integrationId: z.instanceof(ObjectId),
});

const AiFieldDefinitionConfigBaseSchema = z.object({
  analysisFields: AnalysisFieldSchema.array(),
  // needs to include models we used previously for backwards compatability
  model: z.union([
    z.literal('gpt-4o-mini'),
    z.literal('gpt-4.1'),
    z.literal('gpt-4.1-mini'),
  ]),
  clarifications: z.string().array().optional(),
});

const AiBooleanFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('boolean'),
    prompt: z.string(),
  });
export type AiBooleanFieldDefinitionConfig = z.infer<
  typeof AiBooleanFieldDefinitionConfigSchema
>;

const AiRatingFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('rating'),
    prompt: z.string(),
    min: z.number(),
    max: z.number(),
  });
export type AiRatingFieldDefinitionConfig = z.infer<
  typeof AiRatingFieldDefinitionConfigSchema
>;

export const CategorySchema = z.object({
  name: z.string(),
  definition: z.string(),
  examples: z.string().array(),
});
export type Category = z.infer<typeof CategorySchema>;

const AiCategoryFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('category'),
    categoryName: z.string(),
    categories: CategorySchema.array(),
    maxCategories: z.number().optional(),
  });
export type AiCategoryFieldDefinitionConfig = z.infer<
  typeof AiCategoryFieldDefinitionConfigSchema
>;

export const LegacyAiTeamSizeParamsSchema = z.object({
  include: z.string().array(),
});
export type LegacyAiTeamSizeParams = z.infer<
  typeof LegacyAiTeamSizeParamsSchema
>;

const LegacyAiTeamSizeFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('legacyTeamSize'),
    countries: z.array(RoleSearchCountrySchema).optional(),
    prompt: z.object({
      input: z.string(),
      generatedParams: LegacyAiTeamSizeParamsSchema.optional(),
    }),
  });
export type LegacyAiTeamSizeFieldDefinitionConfig = z.infer<
  typeof LegacyAiTeamSizeFieldDefinitionConfigSchema
>;

export const LegacyAiResearchFieldModeSchema = z.enum([
  'structured',
  'freeform',
  'beast',
]);
export type LegacyAiResearchFieldMode = z.infer<
  typeof LegacyAiResearchFieldModeSchema
>;
export const LegacyAiResearchFieldModeLabels: Record<
  LegacyAiResearchFieldMode,
  string
> = {
  structured: 'Structured',
  freeform: 'Free-form',
  beast: 'Beast',
};

const LegacyAiResearchFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('legacyResearch'),
    mode: LegacyAiResearchFieldModeSchema,
    prompt: z.object({
      input: z.string(),
      plan: z.string().optional(),
    }),
    tools: GptToolSchema.array().min(1),
  });
export type LegacyAiResearchFieldDefinitionConfig = z.infer<
  typeof LegacyAiResearchFieldDefinitionConfigSchema
>;

export const AiResearchFormatSchema = z.union([
  z.object({
    type: z.literal('structured'),
  }),
  z.object({
    type: z.literal('freeform'),
  }),
  z.object({
    type: z.literal('custom'),
    instructions: z.string(),
  }),
]);
export type AiResearchFormat = z.infer<typeof AiResearchFormatSchema>;
export const AiResearchFormatLabels: Record<AiResearchFormat['type'], string> =
  {
    structured: 'Structured',
    freeform: 'Free-form',
    custom: 'Custom',
  };

const AiResearchFieldDefinitionConfigSchema =
  AiFieldDefinitionConfigBaseSchema.extend({
    fieldType: z.literal('research'),
    question: z.string(),
    search: z.object({
      query: z.string(),
      disabled: z.boolean().optional(),
    }),
    format: AiResearchFormatSchema,
  });
export type AiResearchFieldDefinitionConfig = z.infer<
  typeof AiResearchFieldDefinitionConfigSchema
>;

// Note: keep in sync with different field type schemas
export const AiFieldDefinitionConfigSchema = z.union([
  AiBooleanFieldDefinitionConfigSchema,
  AiRatingFieldDefinitionConfigSchema,
  AiCategoryFieldDefinitionConfigSchema,
  AiResearchFieldDefinitionConfigSchema,
  LegacyAiTeamSizeFieldDefinitionConfigSchema,
  LegacyAiResearchFieldDefinitionConfigSchema,
]);
export type AiFieldDefinitionConfig = z.infer<
  typeof AiFieldDefinitionConfigSchema
>;

export const FieldDefinitionConfigSchema = z.union([
  RoleCountFieldDefinitionConfigSchema,
  CrmFieldDefinitionConfigSchema,
  AiFieldDefinitionConfigSchema,
]);

const MyAccountsFilterSchema = z
  .object({
    active: z.literal(true),
    excluded: z.literal(false),
  })
  .strict();
export const isMyAccountsFilter = zodTypeguard(MyAccountsFilterSchema);

export const TaggedAccountsFilterSchema = MyAccountsFilterSchema.extend({
  tags: ObjectIdSchema.array().length(1),
}).strict();
export const isTaggedAccountsFilter = zodTypeguard(TaggedAccountsFilterSchema);

const AiEnrichmentAccountFilterSchema = z.union([
  MyAccountsFilterSchema,
  TaggedAccountsFilterSchema,
]);
export type AiEnrichmentAccountFilter = z.infer<
  typeof AiEnrichmentAccountFilterSchema
>;
export const isAiEnrichmentAccountFilter = zodTypeguard(
  AiEnrichmentAccountFilterSchema
);

const AutoEnrichScheduleSchema = z.object({
  cutoffDate: z.date().optional(),
  schedule: z
    .union([
      z.literal('oneMonth'),
      z.literal('threeMonths'),
      z.literal('sixMonths'),
      z.literal('oneYear'),
    ])
    .optional(),
});
export type AutoEnrichSchedule = z.infer<typeof AutoEnrichScheduleSchema>;

export const RefreshSettingsSchema = z.object({
  accountFilter: AiEnrichmentAccountFilterSchema,
  autoEnrichNewAccounts: z.boolean().optional(),
  autoEnrichSchedule: AutoEnrichScheduleSchema.optional(),
});
export type RefreshSettings = z.infer<typeof RefreshSettingsSchema>;

// Field definitions

const RoleCountFieldDefinitionSchema = BaseFieldDefinitionSchema.extend({
  type: z.literal('roleCount'),
  config: RoleCountFieldDefinitionConfigSchema,
});
export type RoleCountFieldDefinition = z.infer<
  typeof RoleCountFieldDefinitionSchema
>;

export const CrmFieldDefinitionSchema = BaseFieldDefinitionSchema.extend({
  type: z.literal('crm'),
  config: CrmFieldDefinitionConfigSchema,
});
export type CrmFieldDefinition = z.infer<typeof CrmFieldDefinitionSchema>;
export const isCrmFieldDefinition = zodTypeguard(CrmFieldDefinitionSchema);

const AiFieldDefinitionBaseSchema = (dataType: FieldDataType) =>
  BaseFieldDefinitionSchema.extend({
    type: z.literal('ai'),
    agentId: ObjectIdSchema,
    dataType: z.literal(dataType),
  });

export const AiBooleanFieldDefinitionSchema = AiFieldDefinitionBaseSchema(
  'boolean'
).extend({
  config: AiBooleanFieldDefinitionConfigSchema,
});

export const AiRatingFieldDefinitionSchema = AiFieldDefinitionBaseSchema(
  'number'
).extend({
  config: AiRatingFieldDefinitionConfigSchema,
});

// TODO: dataType should support arrays for multi categories
export const AiCategoryFieldDefinitionSchema = AiFieldDefinitionBaseSchema(
  'string'
).extend({
  config: AiCategoryFieldDefinitionConfigSchema,
});
export type AiCategoryFieldDefinition = z.infer<
  typeof AiCategoryFieldDefinitionSchema
>;

export const AiTeamSizeFieldDefinitionSchema = AiFieldDefinitionBaseSchema(
  'number'
).extend({
  config: LegacyAiTeamSizeFieldDefinitionConfigSchema,
});
export type AiTeamSizeFieldDefinition = z.infer<
  typeof AiTeamSizeFieldDefinitionSchema
>;
export const isAiTeamSizeFieldDefinition = zodTypeguard(
  AiTeamSizeFieldDefinitionSchema
);

export const AiResearchFieldDefinitionSchema = AiFieldDefinitionBaseSchema(
  'string'
).extend({
  config: AiResearchFieldDefinitionConfigSchema,
});
export type AiResearchFieldDefinition = z.infer<
  typeof AiResearchFieldDefinitionSchema
>;
export const isAiResearchFieldDefinition = zodTypeguard(
  AiResearchFieldDefinitionSchema
);

export const LegacyAiResearchFieldDefinitionSchema =
  AiFieldDefinitionBaseSchema('string').extend({
    config: LegacyAiResearchFieldDefinitionConfigSchema,
  });
export type LegacyAiResearchFieldDefinition = z.infer<
  typeof LegacyAiResearchFieldDefinitionSchema
>;
export const isLegacyAiResearchFieldDefinition = zodTypeguard(
  LegacyAiResearchFieldDefinitionSchema
);

// TODO: deprecate in favor of isAiResearchFieldDefinition once we remove legacy research fields
export function isResearchField(
  fieldDefinition: unknown
): fieldDefinition is
  | AiResearchFieldDefinition
  | LegacyAiResearchFieldDefinition {
  return (
    isLegacyAiResearchFieldDefinition(fieldDefinition) ||
    isAiResearchFieldDefinition(fieldDefinition)
  );
}

export const AiFieldDefinitionSchema = z.union([
  AiBooleanFieldDefinitionSchema,
  AiRatingFieldDefinitionSchema,
  AiCategoryFieldDefinitionSchema,
  AiTeamSizeFieldDefinitionSchema,
  AiResearchFieldDefinitionSchema,
  LegacyAiResearchFieldDefinitionSchema,
]);

export type AiFieldDefinition = z.infer<typeof AiFieldDefinitionSchema>;
export const isAiFieldDefinition = zodTypeguard(AiFieldDefinitionSchema);

export function isAiNonResearchField(fieldDefinition: FieldDefinition) {
  return (
    isAiFieldDefinition(fieldDefinition) && !isResearchField(fieldDefinition)
  );
}

export type AiFieldType = AiFieldDefinition['config']['fieldType'];

const AiFieldDataTypeSchema = z.union([z.boolean(), z.number(), z.string()]);
export type AiFieldDataType = z.infer<typeof AiFieldDataTypeSchema>;

export const FieldDefinitionSchema = z.union([
  RoleCountFieldDefinitionSchema,
  CrmFieldDefinitionSchema,
  AiFieldDefinitionSchema,
]);
export const isFieldDefinition = zodTypeguard(FieldDefinitionSchema);

export type FieldDefinition = z.infer<typeof FieldDefinitionSchema>;

export const FieldDefinitionTypeLabels: Record<
  FieldDefinition['type'],
  string
> = {
  ai: 'AI',
  crm: 'CRM',
  roleCount: 'Role Count',
};

export function getFieldLabel(fieldDefinition?: FieldDefinition) {
  if (!fieldDefinition) {
    return 'Unknown Field';
  }

  if (!isAiFieldDefinition(fieldDefinition)) {
    return fieldDefinition.label;
  }

  const {fieldType} = fieldDefinition.config;
  // Since AI fields have the same label as the agent it belongs to, add the
  // field type to disambiguate.
  return `${fieldDefinition.label} (${AiFieldLabels[fieldType]})`;
}

export const AiFieldLabels = {
  boolean: 'Boolean',
  category: 'Category',
  legacyResearch: 'Research',
  legacyTeamSize: 'Team Size',
  rating: 'Rating',
  research: 'Research',
} satisfies Record<AiFieldType, string>;

// HACK: we don't currently have a universal concept of a parent EnrichmentFieldAction.
// So in the meantime we just use this as a way to group enrichment values that were
// generated together. https://github.com/keyplay-io/bfdb/pull/1572 has some discussion
// and prototyping around what we might want to implement in the future
export const EnrichmentSourceSchema = z.union([
  z.object({
    type: z.literal('task'),
    id: ObjectIdSchema,
  }),
  z.object({
    type: z.literal('api'),
    id: ObjectIdSchema,
  }),
]);
export type EnrichmentSource = z.infer<typeof EnrichmentSourceSchema>;

export interface FieldMetadata {
  reasoning?: string;
}

export interface EnrichedFieldValue {
  customerId: ObjectId;
  accountId: ObjectId;
  fieldDefinitionId: ObjectId;

  // TODO: maybe this should be required?
  source?: EnrichmentSource;

  timestamp: Date;
  value: unknown;
  metadata?: FieldMetadata;
}

export interface EnrichedFieldPreviewValue {
  customerId: ObjectId;
  accountId: ObjectId;
  fieldDefinitionId?: ObjectId; // missing from previews before 5/14/25
  hash: string;

  timestamp: Date;
  value: unknown;
  metadata?: FieldMetadata;
}

export interface FieldDefinitionAnalysisRunResult {
  customerId: ObjectId;
  accountId: ObjectId;
  analysisRunId: ObjectId;
  testListIds: ObjectId[];
  timestamp: Date;

  value: unknown;
  metadata?: FieldMetadata;
}

const TestListResultsBaseSchema = z.object({
  testListId: ObjectIdSchema,
  status: z.string(),
});

const RunningTestListResultsSchema = TestListResultsBaseSchema.extend({
  status: z.literal('running'),
});

const SuccessTestListResultsSchema = TestListResultsBaseSchema.extend({
  status: z.literal('success'),
  numAccounts: z.number(),
  valueCounts: z.array(
    z.object({
      value: AiFieldDataTypeSchema,
      count: z.number(),
    })
  ),
});

export const TestListResultsSchema = z.union([
  RunningTestListResultsSchema,
  SuccessTestListResultsSchema,
]);
export type TestListResults = z.infer<typeof TestListResultsSchema>;

export const FieldDefinitionAnalysisRunSchema = z.object({
  customerId: ObjectIdSchema,
  fieldDefinition: AiFieldDefinitionSchema,
  timestamp: z.date(),
  results: TestListResultsSchema.array(),
});
export type FieldDefinitionAnalysisRun = z.infer<
  typeof FieldDefinitionAnalysisRunSchema
>;

export const MaxFieldDefinitionAnalysisRunAccounts = 10_000;
export const MaxFieldDefinitionAnalysisRunTestLists = 10;
